import React, { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import Layout from "./components/layout";
import AuthGuard from "./guards/AuthGuard";
import OrganisationGuard from "./guards/OrganisationGuard";
import Page404 from "./rootAuth/404page";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { recaptchaSiteKey } from "./Environment";
import { PlanValidationProvider } from "@/context/paymentContext";

// Statically import pages using smart-webcomponents-react to prevent dynamic element layout issues
import ProjectsList from "./pages/projectsList";
import TaskViews from "./components/views/";
import MyTasks from "./pages/tasks/MyTask";

// Lazy-loaded Components for all other routes
const SuspenseLoader = () => (
  <div className="flex h-screen w-screen items-center justify-center bg-slate-50/50">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600"></div>
  </div>
);

/**
 * Retries a dynamic import with a cache-busting query parameter.
 * This handles the "Failed to fetch dynamically imported module" error
 * that occurs when a new deployment replaces old hashed chunk files
 * while users still have the old index.html cached.
 */
const retryDynamicImport = (
  importFunc: () => Promise<{ default: React.ComponentType<any> }>,
  retries = 1
): Promise<{ default: React.ComponentType<any> }> => {
  return importFunc().catch((error) => {
    if (retries > 0) {
      return new Promise<{ default: React.ComponentType<any> }>((resolve) => {
        setTimeout(() => resolve(retryDynamicImport(importFunc, retries - 1)), 1000);
      });
    }
    const reloadKey = "chunk-reload-" + window.location.pathname;
    const lastReload = sessionStorage.getItem(reloadKey);
    const now = Date.now();

    if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
      sessionStorage.setItem(reloadKey, now.toString());
      window.location.reload();
      return new Promise<{ default: React.ComponentType<any> }>(() => { });
    }

    throw error;
  });
};

const lazyLoad = (importFunc: () => Promise<{ default: React.ComponentType<any> }>) => {
  const Component = lazy(() => retryDynamicImport(importFunc));
  return (props: any) => (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );
};

const Organisation = lazyLoad(() => import("./pages/organisation"));
const OrganisationDetails = lazyLoad(() => import("./pages/organisation/OrganisationDetails"));
const OrganisationRiskSettings = lazyLoad(() => import("./pages/organisation/OrganisationRiskSettings"));
const OrganisationNonWorkingDays = lazyLoad(() => import("./pages/organisation/OrganisationNonWorkingDays"));
const OrganisationKpiSettings = lazyLoad(() => import("./pages/organisation/OrganisationKpiSettings"));
const DefaultTimesheetLines = lazyLoad(() => import("./pages/organisation/DefaultTimesheetLines"));
const Verification = lazyLoad(() => import("./pages/authentication/verification"));
const ForgotPassword = lazyLoad(() => import("./pages/authentication/forgot-password"));
const ResetPassword = lazyLoad(() => import("./pages/authentication/forgot-password/reset-password"));
const DashboardNew = lazyLoad(() => import("./pages/dashboard/DashboardNew"));
const ProjectMember = lazyLoad(() => import("./pages/project-details/projectMember"));
const Terms = lazyLoad(() => import("./pages/terms/terms"));
const PrivacyPolicy = lazyLoad(() => import("./pages/terms/privacyPolicy"));
const ProjectDetails = lazyLoad(() => import("./pages/project-details/ProjectDetails"));
const NewAccountSettings = lazyLoad(() => import("./pages/account-settings/NewAccount"));
const NewLogin = lazyLoad(() => import("./pages/authentication/login/newLogin"));
const CheckoutForm = lazyLoad(() => import("./pages/payment-checkout"));
const RiskManagement = lazyLoad(() => import("./pages/risk-management"));
const Scales = lazyLoad(() => import("./pages/risk-management/scales"));
const PaymentPlans = lazyLoad(() => import("./components/PaymentPlans"));
const ManagerDashboard = lazyLoad(() => import("./pages/dashboard/managerDashboard"));
const ApiDocumentation = lazyLoad(() => import("./components/views/openApidocs"));
const CollaboratorVerification = lazyLoad(() => import("./pages/authentication/verification/varifyCollaborator"));
const ResourceUtilization = lazyLoad(() => import("./pages/projectsList/resourceUtilization"));
const AppClint = lazyLoad(() => import("./pages/organisation/appClinte"));
const ManageAbsence = lazyLoad(() => import("./pages/absence").then(module => ({ default: module.ManageAbsence })));
const MyTimesheet = lazyLoad(() => import("./pages/tasks/MyTimesheet"));
const TimesheetApprovals = lazyLoad(() => import("./pages/tasks/TimesheetApprovals"));
const CraDashboard = lazyLoad(() => import("./pages/tasks/CraDashboard"));
const ProjectBudget = lazyLoad(() => import("./pages/budget/ProjectBudget"));
const ResourceAdministration = lazyLoad(() => import("./pages/organisation/ResourceAdministration").then(module => ({ default: module.ResourceAdministration })));

export const LayoutWithValidationProvider = () => (
  <PlanValidationProvider>
    <Layout />
  </PlanValidationProvider>
);

export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <GoogleReCaptchaProvider
        reCaptchaKey={recaptchaSiteKey}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: "head",
        }}
      >
        <NewLogin />
      </GoogleReCaptchaProvider>
    ),
  },
  {
    path: "/signup",
    element: (
      <GoogleReCaptchaProvider
        reCaptchaKey={recaptchaSiteKey}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: "head",
        }}
      >
        <NewLogin />
      </GoogleReCaptchaProvider>
    ),
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    path: "/term-and-condition",
    element: <Terms />,
  },
  {
    path: "/privacy-policy",
    element: <PrivacyPolicy />,
  },
  {
    path: "/verify-email",
    element: <Verification />,
  },
  {
    path: "/collaborator/:userId",
    element: <CollaboratorVerification />,
  },
  {
    path: "/",
    element: (
      <AuthGuard>
        <LayoutWithValidationProvider />
      </AuthGuard>
    ),
    children: [
      {
        path: "/organisation",
        element: <Organisation />,
      },
      {
        path: "/",
        element: <ManagerDashboard />,
      },
      {
        path: "/projectDashboard/:projectId",
        element: <DashboardNew />,
      },
      {
        path: "/account-settings",
        element: <NewAccountSettings />,
      },
      {
        path: "/project-details/:id",
        element: <ProjectDetails />,
      },
      {
        path: "/checkout/:client_secret",
        element: <CheckoutForm />,
      },
      {
        path: "/organisation/:organisationId/organization-details",
        element: <OrganisationDetails />,
      },
      {
        path: "/organisation/:organisationId/nonworking-days",
        element: <OrganisationNonWorkingDays />,
      },
      {
        path: "/organisation/:organisationId/risk-settings",
        element: <OrganisationRiskSettings />,
      },
      {
        path: "/organisation/:organisationId/kpi-settings",
        element: <OrganisationKpiSettings />,
      },
      {
        path: "/organisation/:organisationId/project-settings",
        element: <OrganisationDetails />,
      },
      {
        path: "/organisation/:organisationId/job-titles",
        element: <OrganisationDetails />,
      },
      {
        path: "/organisation/:organisationId/resource-administration",
        element: <ResourceAdministration />,
      },
      {
        path: "/organisation/:organisationId/organization-member",
        element: <OrganisationDetails />,
      },
      {
        path: "/organisation/:organisationId/manage-absence",
        element: <ManageAbsence />,
      },
      {
        path: "/organisation/:organisationId/api-integration",
        element: <AppClint />,
      },
      {
        path: "/resource-management/:organisationId/capacity-planning",
        element: <ResourceUtilization />,
      },
      {
        path: "/plans",
        element: <PaymentPlans onClose={() => { }} />,
      },
      {
        path: "/organisation/:organisationId/timesheet-settings",
        element: <DefaultTimesheetLines />,
      },
      {
        path: "/",
        element: <OrganisationGuard />,
        children: [
          {
            path: "/projects",
            element: <ProjectsList />,
          },
          {
            path: "/tasks/:projectId",
            element: <TaskViews />,
          },
          {
            path: "/workload/:projectId",
            element: <TaskViews />,
          },
          {
            path: "/members/:projectId",
            element: <ProjectMember />,
          },
          {
            path: "/mytasks",
            element: <MyTasks />,
          },
          {
            path: "/my-timesheet",
            element: <MyTimesheet />,
          },
          {
            path: "/timesheet-approvals",
            element: <TimesheetApprovals />,
          },
          {
            path: "/cra-dashboard",
            element: <CraDashboard />,
          },
          {
            path: "/risk/:projectId",
            element: <RiskManagement />,
          },
          {
            path: "/risk/scales",
            element: <Scales />,
          },
          {
            path: "/budget/:projectId",
            element: <ProjectBudget />,
          },
        ],
      },
    ],
  },
  {
    path: "docs",
    element: <ApiDocumentation />,
  },
  {
    path: "*",
    element: <Page404 />,
  },
]);
